import {
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";

import { loadPreferredLang } from "@/i18n/preferredLang";
import { isLocalhost } from "../utils/isLocalhost";

import { auth } from ".";

/**
 * ログイン後に復元する遷移先（ハッシュルート）を退避する sessionStorage キー。
 * 未ログインでディープリンクにアクセスした際、ログイン開始直前に保存し、
 * ログイン後に RedirectToActualTop で読み出して遷移する。
 */
export const POST_LOGIN_REDIRECT_HASH = "postLoginRedirectHash";

/**
 * ログイン後に復元する /o/ 配下の遷移先を退避する sessionStorage キー。
 * 未ログインで /o/ にアクセスすると nginx が /?redirect= へ飛ばすが、
 * ログイン開始直前の replaceState でクエリごと消えるため、ここに保存する。
 * ログイン後に syncStaticFrontSession が読み出して遷移する。
 */
export const POST_LOGIN_STATIC_FRONT_REDIRECT = "postLoginStaticFrontRedirect";

export const STATIC_FRONT_REDIRECT_SEARCH_PREFIX = "?redirect=";

export const firebaseSignIn = async (param?: {
  isReplaceHistory?: boolean;
}) => {
  const isReplaceHistory = param?.isReplaceHistory || false;

  // ログイン開始直前に元のハッシュルートを退避する。
  // リダイレクト経路・localhost ポップアップ経路の両方に効かせるため分岐前に置く。
  // 保存対象は organizations 配下のルートのみに限定する。
  // #/email_verification_settings のように AppAuthProvider が認証前に扱う特殊ハッシュは
  // AppRoutes に対応するルートが無く、保存するとログイン後に 404 へ遷移してしまうため。
  const currentHash = window.location.hash; // 例: "#/organizations?tab=invited"
  if (/^#\/organizations(\/|\?|$)/.test(currentHash)) {
    sessionStorage.setItem(
      POST_LOGIN_REDIRECT_HASH,
      currentHash.replace(/^#/, "")
    );
  }
  // /o/ 配下へのアクセスで nginx が付けた ?redirect= も、下の replaceState で消えるため退避する。
  // 値の解釈は syncStaticFrontSession 側に任せ、ここでは search をそのまま保存する。
  if (window.location.search.startsWith(STATIC_FRONT_REDIRECT_SEARCH_PREFIX)) {
    sessionStorage.setItem(
      POST_LOGIN_STATIC_FRONT_REDIRECT,
      window.location.search
    );
  }

  const provider = new OAuthProvider("oidc.auth0-idp");
  // Auth0 側の画面を選択言語で表示させる (テナントのローカライズ設定が有効な場合のみ効く)。
  // ブラウザ言語からの推定値は本人の表明ではないため送らず、Auth0 のテナント既定に委ねる。
  const uiLocale = loadPreferredLang();
  if (uiLocale !== null) {
    provider.setCustomParameters({ ui_locales: uiLocale });
  }

  /**
   * NOTE: signinWithRedirectはローカルで対応できないため、signInWithPopupを利用
   * https://cloud.google.com/identity-platform/docs/web/redirect-best-practices?hl=ja
   * https://github.com/firebase/firebase-js-sdk/issues/7342
   */
  if (!isLocalhost()) {
    // リダイレクト後に戻ってくる先を変更
    // NOTE: Auth0からのリダイレクト時に余分なパラメータ削除
    isReplaceHistory && history.replaceState(null, document.title, "/");
    // カスタムプロバイダとして、firebaseで設定したauth0テナントのログイン画面にリダイレクトする
    await signInWithRedirect(auth, provider);
  } else {
    const result = await signInWithPopup(auth, provider);
    if (result) {
      location.href = "/";
    }
  }
};
